/**
 * The five stored templates, as a list plus an editor drawer (#1911).
 *
 * A tab on the email-notices page rather than a page of its own: the two
 * surfaces are one job seen from two ends — the copy a notice starts from and
 * the copy the platform sends by itself — and splitting them across routes
 * would mean an operator editing a delist template has to leave the page they
 * came to write a delist notice on.
 *
 * There is no "new" and no "delete" affordance, because the key set is closed.
 * `balance_alert.critical` is not a row an operator should be able to remove:
 * the sender reaches for it at a moment when nobody is watching.
 */
import { useState } from 'react';
import type { EmailTemplate, EmailTemplateKey } from '@nadoutong/contracts';
import { useEmailTemplates, useTestSendEmailTemplate, useUpdateEmailTemplate } from '../../api/useEmailTemplates.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  EmptyState,
  Panel,
  Skeleton,
  Table,
  TableEmpty,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import { EmailTemplateDrawer } from './EmailTemplateDrawer.tsx';
import { templateNameKey } from './templateNames.ts';

export function EmailTemplatesPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const templates = useEmailTemplates();
  const save = useUpdateEmailTemplate();
  const testSend = useTestSendEmailTemplate();
  const [openKey, setOpenKey] = useState<EmailTemplateKey | null>(null);

  const rows: readonly EmailTemplate[] = templates.data ?? [];
  const selected = rows.find((row) => row.key === openKey) ?? null;

  const fail = (error: unknown): void => {
    toast.error(coerceErrorMessage(error, t('email_notices.error.generic')));
  };

  const onSave = (subject: string, body: string): void => {
    if (openKey === null) return;
    save.mutate(
      { key: openKey, body: { subject, body } },
      {
        onSuccess: () => toast.success(t('email_templates.toast.saved')),
        onError: fail,
      },
    );
  };

  const onTestSend = (to: string): void => {
    if (openKey === null) return;
    testSend.mutate(
      { key: openKey, body: { to } },
      {
        onSuccess: () => toast.success(t('email_templates.toast.test_sent', { to })),
        onError: fail,
      },
    );
  };

  return (
    <Panel caption={t('email_templates.panel')} count={rows.length}>
      {templates.isLoading ? (
        <div className="p-4">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : templates.isError ? (
        <div className="p-4">
          <EmptyState
            title={t('email_templates.error.load')}
            hint={t('email_notices.error.load_hint')}
            action={
              <Button variant="secondary" onClick={() => void templates.refetch()}>
                {t('common.retry')}
              </Button>
            }
            testId="email-templates-error"
          />
        </div>
      ) : (
        <Table testId="email-templates-table">
          <THead>
            <Tr interactive={false}>
              <Th>{t('email_templates.column.name')}</Th>
              <Th>{t('email_templates.column.key')}</Th>
              <Th>{t('email_templates.column.subject')}</Th>
              <Th>{t('email_templates.column.updated')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.length === 0 ? (
              <TableEmpty colSpan={4}>{t('email_templates.empty')}</TableEmpty>
            ) : (
              rows.map((row) => (
                <Tr
                  key={row.key}
                  onClick={() => setOpenKey(row.key)}
                  data-testid={`email-templates-row-${row.key}`}
                >
                  <Td emphasis>{t(templateNameKey(row.key))}</Td>
                  <Td mono className="text-paper-low">
                    {row.key}
                  </Td>
                  <Td>{row.subject}</Td>
                  <Td mono className="text-paper-low">
                    {row.updated_at.slice(0, 10)}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      )}

      <EmailTemplateDrawer
        open={openKey !== null}
        onOpenChange={(next) => {
          if (!next) setOpenKey(null);
        }}
        template={selected}
        saving={save.isPending}
        testSending={testSend.isPending}
        onSave={onSave}
        onTestSend={onTestSend}
      />
    </Panel>
  );
}
